import { View, Text, Pressable, Platform } from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

/**
 * Section component for policy content
 */
interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <View className="mb-8">
    <Text className="text-xl font-semibold text-gray-900 mb-3">{title}</Text>
    {children}
  </View>
);

/**
 * Paragraph component for consistent text styling
 */
const Paragraph = ({ children }: { children: React.ReactNode }) => (
  <Text className="text-gray-600 leading-7 mb-3">{children}</Text>
);

/**
 * List item component
 */
const ListItem = ({ children }: { children: React.ReactNode }) => (
  <View className="flex-row items-start gap-3 mb-2">
    <View className="w-2 h-2 rounded-full bg-violet-500 mt-2" />
    <Text className="text-gray-600 leading-7 flex-1">{children}</Text>
  </View>
);

/**
 * Privacy Policy page for the Staatsen Familie App
 */
export default function PrivacyPolicyPage() {
  // Only render on web platform
  if (Platform.OS !== "web") {
    return null;
  }

  const lastUpdated = "2 januari 2026";

  return (
    <View className="flex-1 min-h-screen">
      {/* Header */}
      <View className="bg-gradient-to-r from-violet-600 to-purple-700 px-6 py-12">
        <View className="max-w-2xl mx-auto">
          <Link href="../(web)" asChild>
            <Pressable
              className="flex-row items-center gap-2 mb-6"
              style={{ cursor: "pointer" } as any}
            >
              <Ionicons name="arrow-back" size={20} color="white" />
              <Text className="text-white font-medium">Terug</Text>
            </Pressable>
          </Link>

          <View className="flex-row items-center gap-4">
            <View className="w-14 h-14 bg-white/20 rounded-2xl items-center justify-center">
              <Ionicons name="shield-checkmark" size={28} color="white" />
            </View>
            <View>
              <Text className="text-2xl md:text-3xl font-bold text-white">
                Privacybeleid
              </Text>
              <Text className="text-violet-200 mt-1">
                Laatst bijgewerkt: {lastUpdated}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Content */}
      <View className="flex-1 px-6 py-12">
        <View className="max-w-2xl mx-auto w-full bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <Section title="1. Introductie">
            <Paragraph>
              De Staatsen Familie App ("de App") is een privé familie-applicatie, 
              exclusief beschikbaar voor leden van de Staatsen familie. Deze app 
              is ontworpen om familie-interactie te bevorderen door middel van 
              spelletjes, foto's delen en communicatie.
            </Paragraph>
            <Paragraph>
              We nemen de privacy van onze familieleden, inclusief kinderen, zeer 
              serieus. Dit privacybeleid legt uit welke gegevens we verzamelen, 
              hoe we deze gebruiken en hoe we deze beschermen.
            </Paragraph>
          </Section>

          <Section title="2. Gegevensverzameling">
            <Paragraph>We verzamelen de volgende gegevens:</Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>
                <Text className="font-medium">Accountgegevens:</Text> Naam, 
                e-mailadres en profielfoto om je account te identificeren.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Locatiegegevens:</Text> Alleen 
                tijdens het spelen van de speurtocht, om je voortgang bij te 
                houden. Dit is optioneel.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Foto's:</Text> Foto's die je deelt 
                met de familie worden opgeslagen om gedeeld te kunnen worden.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Spelgegevens:</Text> Scores en 
                voortgang in spelletjes om leaderboards bij te houden.
              </ListItem>
            </View>
          </Section>

          <Section title="3. Kinderen & Minderjarigen">
            <Paragraph>
              Deze app is geschikt voor kinderen en biedt speciale 
              functionaliteit voor jonge familieleden zonder eigen telefoon. 
              Ouders en voogden kunnen:
            </Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>
                Kinderen laten spelen onder hun toezicht via de "Speel als" 
                functie.
              </ListItem>
              <ListItem>
                De activiteiten van hun kinderen monitoren.
              </ListItem>
              <ListItem>
                Op elk moment het account van een kind verwijderen.
              </ListItem>
            </View>
            <Paragraph>
              We verzamelen geen persoonlijke gegevens van kinderen zonder 
              toestemming van een ouder of voogd. Alle accounts worden 
              aangemaakt onder toezicht van een volwassen familielid.
            </Paragraph>
          </Section>

          <Section title="4. Gegevensgebruik">
            <Paragraph>We gebruiken je gegevens uitsluitend om:</Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>De app functionaliteit te bieden</ListItem>
              <ListItem>Spelletjes en scores bij te houden</ListItem>
              <ListItem>Familie-interactie mogelijk te maken</ListItem>
              <ListItem>Push-notificaties te sturen (indien toegestaan)</ListItem>
            </View>
            <Paragraph>
              We verkopen of delen je gegevens NOOIT met derden. De gegevens 
              blijven binnen de familie.
            </Paragraph>
          </Section>

          <Section title="5. Gegevensopslag & Beveiliging">
            <Paragraph>
              Alle gegevens worden veilig opgeslagen via Supabase, een 
              toonaangevende open-source backend-as-a-service provider. 
              Supabase biedt enterprise-grade beveiliging en voldoet aan 
              strenge beveiligingsstandaarden.
            </Paragraph>
            <Paragraph>
              <Text className="font-medium">Supabase beveiligingsmaatregelen:</Text>
            </Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>
                <Text className="font-medium">SOC 2 Type II gecertificeerd:</Text>{" "}
                Supabase voldoet aan strikte beveiligings- en privacynormen.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Encryptie in rust en transit:</Text>{" "}
                Alle data wordt versleuteld opgeslagen (AES-256) en verzonden 
                via HTTPS/TLS.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Row Level Security (RLS):</Text>{" "}
                Database-niveau toegangscontrole zorgt ervoor dat gebruikers 
                alleen hun eigen gegevens kunnen zien.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Beveiligde authenticatie:</Text>{" "}
                Wachtwoorden worden gehasht met bcrypt en nooit in platte 
                tekst opgeslagen.
              </ListItem>
              <ListItem>
                <Text className="font-medium">EU datacenters:</Text>{" "}
                Onze database is gehost in een Europees datacenter voor 
                naleving van AVG/GDPR.
              </ListItem>
            </View>
            <Paragraph>
              Daarnaast hebben wij de volgende maatregelen getroffen:
            </Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>Beperkte toegang alleen voor geverifieerde familieleden</ListItem>
              <ListItem>Regelmatige beveiligingsupdates van de app</ListItem>
              <ListItem>Geen opslag van onnodige persoonsgegevens</ListItem>
            </View>
          </Section>

          <Section title="6. App Permissies">
            <Paragraph>De app vraagt om de volgende permissies:</Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>
                <Text className="font-medium">Camera:</Text> Voor foto's maken 
                en QR-codes scannen tijdens spelletjes.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Fotobibliotheek:</Text> Om foto's 
                te uploaden en op te slaan.
              </ListItem>
              <ListItem>
                <Text className="font-medium">Locatie:</Text> Alleen voor de 
                speurtocht functie (optioneel).
              </ListItem>
              <ListItem>
                <Text className="font-medium">Notificaties:</Text> Om je te 
                informeren over familie-activiteiten.
              </ListItem>
            </View>
          </Section>

          <Section title="7. Je Rechten">
            <Paragraph>Je hebt het recht om:</Paragraph>
            <View className="ml-2 mb-4">
              <ListItem>Je gegevens in te zien</ListItem>
              <ListItem>Je gegevens te laten corrigeren</ListItem>
              <ListItem>Je account te verwijderen</ListItem>
              <ListItem>Je toestemming in te trekken</ListItem>
            </View>
          </Section>

          <Section title="8. Account Verwijderen">
            <Paragraph>
              Je kunt op elk moment je account verwijderen via de app 
              (Profiel → Instellingen → Account Verwijderen) of door contact 
              met ons op te nemen. Bij verwijdering worden al je persoonlijke 
              gegevens permanent verwijderd.
            </Paragraph>
          </Section>

          <Section title="9. Contact">
            <Paragraph>
              Vragen over dit privacybeleid? Neem contact op met de 
              familie-beheerder of stuur een e-mail naar:{" "}
              <Text className="text-violet-600 font-medium">
                jesse.staats@gmail.com
              </Text>
            </Paragraph>
          </Section>

          {/* Bottom note */}
          <View className="mt-8 p-4 bg-gray-50 rounded-xl">
            <Text className="text-sm text-gray-500 text-center">
              Dit privacybeleid kan worden bijgewerkt. Controleer regelmatig 
              deze pagina voor de laatste versie.
            </Text>
          </View>
        </View>
      </View>

      {/* Footer */}
      <View className="bg-gray-50 border-t border-gray-200 px-6 py-8">
        <View className="max-w-2xl mx-auto items-center">
          <Text className="text-sm text-gray-500 text-center">
            © {new Date().getFullYear()} Staatsen Familie App
          </Text>
        </View>
      </View>
    </View>
  );
}

